import React from "react";
import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_SURVEY_PERIOD_DAYS,
  getDefaultSurveyPeriod,
  isFullHistoryPeriod,
  matchesDefaultSurveyPeriod,
} from "./surveyPeriod";

const SurveyPeriodFilterBar = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onResetDefault,
  onViewAllHistory,
  extraActions = null,
  compact = false,
}) => {
  const { t } = useTranslation("surveys");
  const isDefault = matchesDefaultSurveyPeriod(startDate, endDate);
  const isFullHistory = isFullHistoryPeriod(startDate, endDate);

  const handleResetDefault = () => {
    const period = getDefaultSurveyPeriod();
    onResetDefault?.(period);
    onStartDateChange?.(period.startDate);
    onEndDateChange?.(period.endDate);
  };

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant={compact ? "body2" : "subtitle2"} color="text.secondary">
          {t("period.label")}
        </Typography>
        {isFullHistory ? (
          <Chip size="small" label={t("period.fullHistory")} color="default" variant="outlined" />
        ) : isDefault ? (
          <Chip
            size="small"
            label={t("period.defaultChip", { days: DEFAULT_SURVEY_PERIOD_DAYS })}
            color="primary"
            variant="outlined"
          />
        ) : (
          <Chip size="small" label={t("period.customChip")} color="warning" variant="outlined" />
        )}
      </Stack>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "flex-end" }}
      >
        <TextField
          label={t("period.start")}
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(event) => onStartDateChange(event.target.value)}
          sx={{ minWidth: compact ? undefined : 160 }}
        />
        <TextField
          label={t("period.end")}
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(event) => onEndDateChange(event.target.value)}
          sx={{ minWidth: compact ? undefined : 160 }}
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          {!isDefault ? (
            <Button variant="outlined" size="small" onClick={handleResetDefault}>
              {t("period.resetDefault", { days: DEFAULT_SURVEY_PERIOD_DAYS })}
            </Button>
          ) : null}
          {!isFullHistory ? (
            <Button
              variant="text"
              size="small"
              onClick={() => {
                onViewAllHistory?.();
                onStartDateChange("");
                onEndDateChange("");
              }}
            >
              {t("period.viewAllHistory")}
            </Button>
          ) : null}
          {extraActions}
        </Box>
      </Stack>
      {!compact ? (
        <Typography variant="caption" color="text.secondary">
          {isFullHistory ? t("period.fullHistoryHelper") : t("period.defaultHelper", { days: DEFAULT_SURVEY_PERIOD_DAYS })}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default SurveyPeriodFilterBar;
